// Shared helpers for the vibe/ components (WEB-VIBE). No network calls here.

import { useEffect, useRef } from 'react';
import { bandByKey } from '../bands';

// bands.js object or band key -> bands.js object (or null)
export const resolveBand = (band) => (typeof band === 'string' ? bandByKey(band) || null : band || null);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'YYYY-MM-DD' (or a dateKey) -> 'May 8, 1977'
export const fmtDate = (iso) => {
  const m = /^(\d{4})[-_](\d{2})[-_](\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const mo = parseInt(m[2], 10);
  if (mo < 1 || mo > 12) return m[1];
  return MONTHS[mo - 1] + ' ' + parseInt(m[3], 10) + ', ' + m[1];
};

export const yearOf = (iso) => {
  const m = /^(\d{4})/.exec(String(iso || ''));
  return m ? parseInt(m[1], 10) : null;
};

// The browse show key for an iso date (contract B.0): archive bands use
// 'YYYY-MM-DDT00:00:00Z', WSP 'YYYY_MM_DD'. Only used when a server row has
// no dateKey of its own.
export const dateKeyFor = (bandKey, iso) => {
  const d = String(iso || '').substring(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  return bandKey === 'wsp' ? d.replace(/-/g, '_') : d + 'T00:00:00Z';
};

// seconds -> 'm:ss' or 'h:mm:ss'
export const fmtSecs = (secs) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h ? h + ':' + pad(m) + ':' + pad(r) : m + ':' + pad(r);
};

export const fmtCount = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '');

export const plural = (n, one, many) => fmtCount(n) + ' ' + (n === 1 ? one : many || one + 's');

// Safe reads of meta.features with the B.2 shape and conservative defaults.
export const featuresOf = (meta) => {
  const f = (meta && meta.features) || {};
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
  return {
    sbd: !!f.sbd,
    fingerprint: obj(f.fingerprint),
    vibes: Array.isArray(f.vibes) ? f.vibes.filter((v) => v && typeof v.key === 'string' && v.key) : [],
    radio: obj(f.radio),
    surprise: obj(f.surprise),
  };
};

export const erasOf = (meta) =>
  meta && Array.isArray(meta.eras) ? meta.eras.filter((e) => e && typeof e.key === 'string' && e.key && e.label) : [];

// "Arrows are rare" for a band: the Flow axis is switched off band-wide
// (contract C.5: fewer than 30% of shows carry usable into-transition data).
// The meta has no dedicated flag, so this is the signal. Copy then talks
// about "songs" rather than "runs of songs that went into each other".
export const arrowsRare = (meta) => !(meta && meta.features && meta.features.fingerprint && meta.features.fingerprint.flow);

// Closes a popover or panel on Escape, and (when `rootRef` is given) on a
// mouse or touch press outside it. Active only while `active` is true.
export const useDismiss = (active, onDismiss, rootRef) => {
  const cb = useRef(onDismiss);
  cb.current = onDismiss;
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      // react-select handles its own Escape (closing its menu) and marks it
      if (e.defaultPrevented) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        // handled here: App's page-wide Escape (close the open panel) skips it
        e.preventDefault();
        cb.current(e);
      }
    };
    const onDown = (e) => {
      const root = rootRef && rootRef.current;
      if (root && !root.contains(e.target)) cb.current(e);
    };
    document.addEventListener('keydown', onKey);
    if (rootRef) {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('touchstart', onDown);
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      if (rootRef) {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('touchstart', onDown);
      }
    };
  }, [active, rootRef]);
};

// Stable unique ids for aria wiring.
let idSeq = 0;
export const useUid = (prefix) => {
  const r = useRef(null);
  if (r.current == null) {
    idSeq += 1;
    r.current = (prefix || 'vb') + '-' + idSeq;
  }
  return r.current;
};

// Arrow-key movement inside a group of buttons (tabs, chips).
export const arrowNav = (e) => {
  const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  const dir = keys[e.key];
  if (!dir) return;
  const group = e.currentTarget;
  const items = Array.prototype.slice.call(group.querySelectorAll('button:not([disabled])'));
  const at = items.indexOf(document.activeElement);
  if (at < 0 || !items.length) return;
  e.preventDefault();
  const next = items[(at + dir + items.length) % items.length];
  next.focus();
  const role = group.getAttribute('role');
  if (role === 'tablist' || role === 'radiogroup') next.click();
};

// Copy for the named vibes (contract C.5). Unknown keys get no description.
export const VIBE_BLURBS = {
  exploratory: 'Long, open jams that wander',
  flowing: 'Songs that went into each other',
  stretched: 'Songs played well past their usual length',
  'deep-cuts': 'Rare songs and oddities',
  bustouts: 'Songs back after a long break',
  crowd: 'The most loved shows',
  pristine: 'The best soundboard tapes',
  space: 'Plenty of free-form space',
  cowboy: 'Country and cowboy songs',
  ballads: 'Slow songs and ballads',
  rockers: 'Rockers and anthems',
  bluegrass: 'Bluegrass picking',
  bluesy: 'Blues numbers',
};

export const ORDER_LABELS = { chrono: 'In order', monster: 'Longest first', shuffle: 'Shuffle' };
export const ORDER_HINTS = {
  chrono: 'Every version, oldest first',
  monster: 'The longest versions first',
  shuffle: 'Every version, in random order',
};
